import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Search, Plus, AlertTriangle, UserCheck, Trash2, ShieldAlert } from 'lucide-react';
import { listAllContactsForPicker, type ManualRecipient } from '../lib/supabase';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  current: ManualRecipient[];
  onSave: (next: ManualRecipient[]) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ManualRecipientPicker({ isOpen, onClose, current, onSave }: Props) {
  const [tab, setTab] = useState<'contacts' | 'free'>('contacts');
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<ManualRecipient[]>(current);

  // Free-Eintrag-Inputs
  const [freeEmail, setFreeEmail] = useState('');
  const [freeFirst, setFreeFirst] = useState('');
  const [freeLast, setFreeLast] = useState('');
  const [freeConsent, setFreeConsent] = useState(false);

  const contactsQuery = useQuery({
    queryKey: ['all-contacts-picker'],
    queryFn: listAllContactsForPicker,
    enabled: isOpen && tab === 'contacts',
  });

  const draftEmails = useMemo(() => new Set(draft.map(d => d.email.toLowerCase())), [draft]);

  const filteredContacts = useMemo(() => {
    const list = contactsQuery.data ?? [];
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(c =>
      c.email.toLowerCase().includes(q) ||
      (c.first_name?.toLowerCase().includes(q) ?? false) ||
      (c.last_name?.toLowerCase().includes(q) ?? false)
    );
  }, [contactsQuery.data, search]);

  const addContact = (c: { email: string; first_name: string | null; last_name: string | null; opt_in: boolean; unsubscribed_at: string | null; bounce_status: string | null }) => {
    if (draftEmails.has(c.email.toLowerCase())) return;
    // Bei bekannten Kontakten gilt das DSGVO-Opt-In aus dem CRM. Wir markieren has_consent_confirmed=true,
    // wenn der Kontakt opt_in=true und nicht abgemeldet ist. Sonst muss der Nutzer aktiv bestätigen
    // (das passiert dann unten in der Liste — der Eintrag wird "rot" angezeigt).
    const hasContactConsent = !!c.opt_in && !c.unsubscribed_at && c.bounce_status !== 'hard';
    setDraft([...draft, {
      email: c.email,
      first_name: c.first_name,
      last_name: c.last_name,
      source: 'contact',
      has_consent_confirmed: hasContactConsent,
    }]);
  };

  const addFreeEntry = () => {
    const email = freeEmail.trim();
    if (!EMAIL_RE.test(email)) {
      alert('Bitte eine gültige E-Mail-Adresse eintragen.');
      return;
    }
    if (!freeConsent) {
      alert('Bitte bestätige zuerst, dass die Person dem Erhalt von Werbe-E-Mails ausdrücklich zugestimmt hat.');
      return;
    }
    if (draftEmails.has(email.toLowerCase())) {
      alert('Diese E-Mail ist bereits in der Liste.');
      return;
    }
    setDraft([...draft, {
      email, first_name: freeFirst || null, last_name: freeLast || null,
      source: 'manual', has_consent_confirmed: true,
    }]);
    setFreeEmail(''); setFreeFirst(''); setFreeLast(''); setFreeConsent(false);
  };

  const removeAt = (idx: number) => {
    const next = [...draft]; next.splice(idx, 1); setDraft(next);
  };

  const toggleConsent = (idx: number) => {
    const next = [...draft];
    next[idx] = { ...next[idx], has_consent_confirmed: !next[idx].has_consent_confirmed };
    setDraft(next);
  };

  if (!isOpen) return null;

  const blockedCount = draft.filter(d => !d.has_consent_confirmed).length;

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg" style={{ maxWidth: 820 }}>
        <div className="modal-header">
          <h2 className="modal-title">Einzelne Empfänger hinzufügen</h2>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-content">
          <p style={{ margin: '0 0 1rem', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
            Empfänger zusätzlich zur Gruppen-Auswahl. Du kannst entweder bestehende Kontakte aus
            der Datenbank wählen oder freie E-Mail-Adressen eintragen.
          </p>

          {/* DSGVO-Warnung */}
          <div className="alert alert-warning" style={{ marginBottom: '1.25rem' }}>
            <ShieldAlert size={18} />
            <div>
              <strong>Wichtig zum Versand an manuelle Adressen:</strong> Eine E-Mail-Marketing-Versand
              an Personen ohne nachweisbares <strong>Opt-In</strong> ist rechtlich heikel (DSGVO,
              UWG-§7) und schadet zusätzlich dem <strong>Spam-Score unserer Domain</strong> —
              jede Hard-Bounce, jede Spam-Beschwerde und jede Abmeldung verschlechtert die
              Zustellrate aller folgenden Kampagnen, auch an gut gepflegte Empfänger. Bevor du
              eine fremde E-Mail-Adresse einträgst, hol dir bitte aktiv die Einwilligung
              (z.B. per Antwort-Mail) und lege die Person danach in <a href="/contacts" style={{ color: 'var(--primary)' }}>Kontakten</a> mit Opt-In an.
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '0.25rem', background: 'var(--surface-secondary)', padding: '0.25rem', borderRadius: 'var(--radius-md)', marginBottom: '1rem' }}>
            <button
              className={`step-item${tab === 'contacts' ? ' active' : ''}`}
              onClick={() => setTab('contacts')}
              style={{ fontSize: '0.875rem', flex: 1 }}>
              <UserCheck size={14} /> Aus Kontakten wählen
            </button>
            <button
              className={`step-item${tab === 'free' ? ' active' : ''}`}
              onClick={() => setTab('free')}
              style={{ fontSize: '0.875rem', flex: 1 }}>
              <Plus size={14} /> Frei eintragen
            </button>
          </div>

          {tab === 'contacts' && (
            <div>
              <div className="search-input-wrapper" style={{ marginBottom: '0.75rem' }}>
                <Search size={16} />
                <input className="search-input"
                  placeholder="Kontakt suchen (Name oder E-Mail)..."
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              {contactsQuery.isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                  <div className="loading-spinner" />
                </div>
              ) : filteredContacts.length === 0 ? (
                <div className="empty-state" style={{ padding: '1.5rem' }}>
                  <p>Kein Kontakt gefunden.</p>
                </div>
              ) : (
                <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)' }}>
                  {filteredContacts.map(c => {
                    const inDraft = draftEmails.has(c.email.toLowerCase());
                    const ineligible = !c.opt_in || c.unsubscribed_at || c.bounce_status === 'hard';
                    return (
                      <div key={c.id} style={{
                        display: 'flex', alignItems: 'center', gap: '0.75rem',
                        padding: '0.5rem 0.75rem',
                        borderBottom: '1px solid var(--border-light)',
                        opacity: ineligible ? 0.55 : 1,
                      }}>
                        <div style={{ flex: 1, fontSize: '0.875rem' }}>
                          <div style={{ fontFamily: 'monospace' }}>{c.email}</div>
                          <div style={{ color: 'var(--text-tertiary)', fontSize: '0.75rem' }}>
                            {[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}
                            {ineligible && <> · <span style={{ color: 'var(--danger-dark)' }}>kein Opt-In / abgemeldet / Bounce</span></>}
                          </div>
                        </div>
                        <button className={`btn ${inDraft ? 'btn-secondary' : 'btn-primary'} btn-sm`}
                          onClick={() => !inDraft && addContact(c)} disabled={inDraft}>
                          {inDraft ? 'Bereits dabei' : <><Plus size={13} /> Hinzufügen</>}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === 'free' && (
            <div className="card" style={{ background: 'var(--surface-secondary)' }}>
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label className="form-label">E-Mail *</label>
                  <input className="form-input" type="email" placeholder="name@beispiel.de"
                    value={freeEmail} onChange={(e) => setFreeEmail(e.target.value)} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Vorname</label>
                  <input className="form-input" value={freeFirst} onChange={(e) => setFreeFirst(e.target.value)} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Nachname</label>
                  <input className="form-input" value={freeLast} onChange={(e) => setFreeLast(e.target.value)} />
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.625rem', padding: '0.5rem 0', cursor: 'pointer', userSelect: 'none' }}>
                <input type="checkbox" checked={freeConsent} onChange={(e) => setFreeConsent(e.target.checked)}
                  style={{ accentColor: 'var(--primary)', width: 18, height: 18, marginTop: 2 }} />
                <span style={{ fontSize: '0.875rem' }}>
                  <strong>Einwilligungs-Bestätigung *</strong>
                  <div style={{ color: 'var(--text-tertiary)', marginTop: 2 }}>
                    Ich bestätige, dass diese Person dem Erhalt von Marketing-/Werbe-E-Mails
                    von Randori-Pro aktiv und nachweisbar zugestimmt hat
                    (z.B. Anmeldeformular mit Double-Opt-In, schriftliche Zustimmung).
                  </div>
                </span>
              </label>
              <button className="btn btn-primary" onClick={addFreeEntry}
                disabled={!EMAIL_RE.test(freeEmail.trim()) || !freeConsent}>
                <Plus size={14} /> Zur Liste hinzufügen
              </button>
            </div>
          )}

          {/* Aktuelle Liste */}
          <h3 style={{ margin: '1.5rem 0 0.5rem', fontSize: '0.95rem' }}>
            Manuell hinzugefügt: {draft.length} Empfänger
            {blockedCount > 0 && <span style={{ color: 'var(--danger-dark)', marginLeft: 8, fontSize: '0.8125rem' }}>
              ({blockedCount} ohne Einwilligung — werden NICHT versendet)
            </span>}
          </h3>
          {draft.length === 0 ? (
            <p style={{ color: 'var(--text-tertiary)', fontSize: '0.875rem' }}>Noch keine manuellen Empfänger.</p>
          ) : (
            <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)' }}>
              {draft.map((d, idx) => (
                <div key={`${d.email}-${idx}`} style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.5rem 0.75rem',
                  borderBottom: idx < draft.length - 1 ? '1px solid var(--border-light)' : 'none',
                  background: !d.has_consent_confirmed ? 'var(--danger-light)' : undefined,
                }}>
                  <div style={{ flex: 1, fontSize: '0.875rem' }}>
                    <div style={{ fontFamily: 'monospace' }}>
                      {d.email}
                      {d.source === 'manual' && (
                        <span className="status-badge" style={{ marginLeft: 8, background: 'var(--warning-light)', color: 'var(--warning-dark)' }}>
                          frei eingetragen
                        </span>
                      )}
                    </div>
                    <div style={{ color: 'var(--text-tertiary)', fontSize: '0.75rem' }}>
                      {[d.first_name, d.last_name].filter(Boolean).join(' ') || '—'}
                    </div>
                  </div>
                  {!d.has_consent_confirmed && (
                    <button className="btn btn-ghost btn-sm" onClick={() => toggleConsent(idx)}
                      title="Einwilligung bestätigen (auf eigene Verantwortung)">
                      <AlertTriangle size={13} style={{ color: 'var(--danger-dark)' }} /> Einwilligung bestätigen
                    </button>
                  )}
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }}
                    onClick={() => removeAt(idx)} title="Entfernen">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid var(--border-light)' }}>
            <button className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
            <button className="btn btn-primary" onClick={() => { onSave(draft); onClose(); }}>
              Übernehmen ({draft.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
